import {
  FieldHtmlNameUtil,
  type CadPlanId,
  type WaParsedMetaData,
  type WaEventData,
  BuildTags,
} from '@onshape/www-inertia';
import { useDebounceFn } from '@vueuse/core';
import { isObjectsEqual } from '~/utils/isObjectsEqual';
import { TextEncodingUtil } from '@onshape/www-inertia';

type HandledEvent = { isHandled: boolean };

const userDecorationsPushThrottle = 1000 * 60 * 60;
const haveSeenOn = ref(false);
const signupStage = ref('');
const signupPage = ref('');
const signupPlan = ref('');
const signupDt = ref('');
const signupNumAttempts = ref('');

/** push an event to /wa */
type WaEventName =
  | 'WaPageView'
  | 'WaPageUnload'
  | 'UssScreenShown'
  | 'FormSuccessfullySubmitted'
  | 'PersonaInfo'
  | 'UtmParams';

const waBandEventNames = ['from_submit_enhance_in_network'];

/** events queued to be pushed to /wa */
const waQueue: Array<WaEventData> = [];
/** events that should be sent immediately */
const instantEvents = ['WaPageUnload'];

// random nounce for this page load
const waPageLoadNounce = `${Date.now()}-${Math.floor(Math.random() * 1e10).toString(16)}`;

const waUtmParamNames = [
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
] as const;
const waUtmParamValues: Record<string, string> = {};
// probablity the user will be push to wa, set to -1 to say not initialized (e.g. when developing and client side query)
let waThrottleRate = -1;

// user agents that should be banned from wa
const waBandUserAgents: Array<RegExp> = [/OneTrust/i];
// max pushed per page
let waMaxPushFailSafePerPage = 25;

// max push globally
const waGlobalMaxPushFailSafe = 1000;

/** Create an HekateClickEvent for GA */
const onClickBody = (evt: MouseEvent) => {
  if (!evt.target || !(evt.target instanceof Element)) {
    return;
  }

  if ((evt as unknown as HandledEvent).isHandled) {
    return;
  }

  const elm = evt.target;
  const data: { [key: string]: string | null } = {};
  const attributePrefix = 'clk-dec-';
  let href = '';
  let p = elm;
  while (p.parentElement) {
    const cur = p;
    if (!href) {
      href = cur.getAttribute('href') || '';
    }

    cur
      .getAttributeNames()
      .filter((name) => name.startsWith(attributePrefix))
      .forEach((name) => {
        const val = cur.getAttribute(name);
        if (!val) return;
        const key = `clk-dec-${name.substring(attributePrefix.length)}`;
        data[key] = val;
      });
    p = p.parentElement;
  }

  if (!href) {
    return;
  }
  // set the evt has "handled" to avoid double pushing
  (evt as unknown as HandledEvent).isHandled = true;

  let { textContent } = elm;
  ['alt', 'title', 'class'].forEach((attrName) => {
    if (textContent) return;
    const val = elm.getAttribute(attrName);
    if (!val) return;
    textContent = `${attrName}: ${val}`;
  });
  textContent = textContent || '';
  textContent = textContent.replace(/\s+/g, ' ');
  textContent = textContent.length <= 140 ? textContent : `${textContent.substring(0, 140)}...`;
  textContent = textContent && textContent.trim() ? textContent.trim() : 'unknown';

  // If you add more decorations you MUST contact whoever is managing GoogleTagManager
  const toPush = {
    ...data,
    'clk-dec-href': href,
    'clk-dec-note': textContent,
  };

  dataLayerPush({
    event: 'HekateLinkClick',
    ...toPush,
  });
};

/** init click tracking */
const initClickTracking = ({ waThrottle }: { waThrottle?: number }) => {
  if (waThrottle || waThrottle === 0) {
    waThrottleRate = waThrottle;
  } else {
    console.warn('waThrottle not set, defaulting to 1.0');
    waThrottleRate = 1.0;
  }

  if (import.meta.server || !window || !document.body) {
    return;
  }
  document.body.addEventListener('click', onClickBody);
};

/** wrapper around window._mfq.push() */
const mouseFlowPush = (toPush: (string | boolean)[]): void => {
  if (import.meta.server || !window || !window._mfq) return;
  window._mfq = window._mfq || [];
  window._mfq.push(toPush);
};

/** safely wrappers around window.dataLayer */
const dataLayerPush = (data: { [key: string]: string; event: string }) => {
  if (import.meta.server || !window || !window.wwwGaIsReadyPromise) return;

  waPush(data);

  window.wwwGaIsReadyPromise.then(() => {
    window.ptcDebug = window.ptcDebug || {};
    window.ptcDebug.dataLayerPushes = window.ptcDebug.dataLayerPushes || [];
    window.ptcDebug.dataLayerPushes.push(data);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  });
};

const waSignature = ({ tag, data, meta }: { tag: string; data: string; meta: string }): string => {
  let sum = 0;
  for (const ii of [tag, data, meta]) {
    for (let jj = 0; jj < ii.length; jj += 1) {
      sum += ii.charCodeAt(jj);
    }
  }
  const parity = sum % 1024;
  return `sigv1-${Date.now()}-${sum}-${parity}`;
};

const waPush = async (data: {
  [key: string]: string | boolean | number;
  event: WaEventName | string;
}): Promise<void> => {
  if (import.meta.server) return;

  if (waBandEventNames.includes(data.event)) {
    return;
  }

  waQueue.push({ data, now: Date.now() });

  if (instantEvents.includes(data.event)) {
    if (useEnvContext().isDev) {
      console.log('waPush instant event, sending immediately:', data.event);
    }
    waSendRequest({
      isInstantEvent: true,
    });
    return;
  }

  if (TextEncodingUtil.base64Encode(JSON.stringify(waQueue)).length > 5000) {
    if (useEnvContext().isDev) {
      console.log('waPush queue too large, sending now');
    }
    waSendRequest({
      isInstantEvent: true,
    });
    return;
  }

  waPushDebounced();
};

const waPushDebounced = useDebounceFn(() => {
  waSendRequest();
}, 3500);

const waSendRequest = async ({
  isInstantEvent,
}: { isInstantEvent?: boolean } = {}): Promise<void> => {
  if (import.meta.server) return;

  if (waQueue.length === 0) {
    console.log('waPush: no events to send');
    return;
  }

  if (waBandUserAgents.some((re) => re.test(navigator.userAgent))) {
    if (useEnvContext().isDev) {
      console.log(`waPush: user agent is banded, not sending: ${navigator.userAgent}`);
    }
    return;
  }

  // check global max pushes
  const globalNumPushes = useCookie('x-www-wa-num-pushes').value ?? '0';
  if (parseInt(globalNumPushes) >= waGlobalMaxPushFailSafe) {
    console.warn(
      `waPush: max global push fail safe reached, not sending more requests: ${globalNumPushes} max: ${waGlobalMaxPushFailSafe}`
    );
    return;
  }

  const { getFromLocalStorageOrNull, setToLocalStorage } = useWwwStorage();

  const waThrottled = getFromLocalStorageOrNull<number>({ key: 'WaThrottled' });
  if (waThrottled === null && waThrottleRate >= 0) {
    const rand = Math.random();
    const isThrottled = rand >= waThrottleRate ? 1 : 0;
    setToLocalStorage<number>({ key: 'WaThrottled', data: isThrottled, ttl: 1000 * 60 * 60 * 24 });
  }
  if (waThrottled === 1) {
    if (useEnvContext().isDev) {
      console.log('waPush: throttled, not sending');
    }
    return;
  }

  let waTag = getFromLocalStorageOrNull<string>({ key: 'WaTag' });
  if (!waTag) {
    const newTag = `v1-${Date.now()}-${Math.floor(Math.random() * 1e10).toString(16)}`;
    setToLocalStorage<string>({ key: 'WaTag', data: newTag, ttl: 1000 * 60 * 60 * 24 });
    waTag = getFromLocalStorageOrNull<string>({ key: 'WaTag' });
  }
  if (!waTag) {
    console.warn('waPush: unable to get or set WaTag, aborting waPush');
    return;
  }

  if (waMaxPushFailSafePerPage <= 0) {
    console.warn('waSendRequest: max push fail safe reached, not sending more requests');
    return;
  }
  waMaxPushFailSafePerPage -= 1;

  const toPush = [...waQueue];
  waQueue.splice(0, waQueue.length);

  const events = toPush.map((item) => item.data.event).join(',');
  const dataEncoded = TextEncodingUtil.base64Encode(JSON.stringify(toPush || {}));

  const meta: WaParsedMetaData = {
    url: window.location.href,
    numEvents: toPush.length,
  };
  const metaEncoded = TextEncodingUtil.base64Encode(JSON.stringify(meta));

  const sig = waSignature({ tag: waTag, data: dataEncoded, meta: metaEncoded });

  const waHeaders = {
    'x-wa-tag': waTag,
    'x-wa-events': events,
    'x-wa-data': dataEncoded,
    'x-wa-sig': sig,
    'x-wa-load': waPageLoadNounce,
    'x-wa-meta': metaEncoded,
  };

  if (useEnvContext().isDev) {
    if (events.includes('WaPageUnload')) {
      console.log('waPush', JSON.stringify({ toPush }));
    } else {
      console.log('waPush', { data: toPush });
    }
  }

  try {
    const nounce = `${Date.now()}-${Math.floor(Math.random() * 1e10).toString(16)}`;
    if (isInstantEvent) {
      const blob = new Blob([JSON.stringify(waHeaders)], { type: 'application/json' });
      navigator.sendBeacon(new URL(`/wa?nounce=${nounce}`, window.location.origin), blob);
    } else {
      const result = await useWwwFetch<{ msg: string }>({
        url: new URL(`/wa?nounce=${nounce}`, window.location.origin),
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...waHeaders,
        },
      });
      if (result && result.msg) {
        useCookie('x-www-wa-status').value = `${result.msg}`;
      }
    }
  } catch (e) {
    console.error('waPush error:', e);
  } finally {
    useCookie('x-www-wa-num-pushes').value = `${parseInt(globalNumPushes) + 1}`;
  }
};

const cookieUpdates = () => {
  const { isDeveloper } = useEnvContext();
  const { personalizationProfileStorable, getCookieStringByName } = useWwwStorage();

  if (isDeveloper) {
    mouseFlowPush(['tag', 'webdev']);
  }

  const cookieHaveSeenOn = !!getCookieStringByName('x-www-have-seen-on');
  if (cookieHaveSeenOn && cookieHaveSeenOn !== haveSeenOn.value) {
    mouseFlowPush(['tag', 'onshape-user']);
  }
  haveSeenOn.value = cookieHaveSeenOn;

  if (personalizationProfileStorable.value?.currentUserData.activePlanGroup) {
    mouseFlowPush([
      'setVariable',
      'plan-group',
      personalizationProfileStorable.value.currentUserData.activePlanGroup,
      'session',
      true,
    ]);
  }

  // See if we should clear 'signup-stage' data
  if (
    haveSeenOn &&
    personalizationProfileStorable.value?.signupDecoration &&
    personalizationProfileStorable.value?.signupDecoration.stage !== 'have-logged-in'
  ) {
    personalizationProfileStorable.value.signupDecoration.stage = 'have-logged-in';
    mouseFlowPush(['setVariable', 'signup-stage', 'have-logged-in', 'session', false]);
    personalizationProfileStorable.value.signupDecoration = undefined;
  }

  if (personalizationProfileStorable.value?.signupDecoration) {
    signupStage.value = personalizationProfileStorable.value.signupDecoration.stage;
    signupPage.value = `${personalizationProfileStorable.value.signupDecoration.page}`;
    signupPlan.value = personalizationProfileStorable.value.signupDecoration.planId;
    const signupDecorationDateAdded =
      personalizationProfileStorable.value.signupDecoration.dateAdded || 0;
    signupDt.value = ((Date.now() - signupDecorationDateAdded) / (1000 * 60 * 60)).toFixed(1);
    signupNumAttempts.value = `${personalizationProfileStorable.value.signupDecoration.numAttempts}`;

    mouseFlowPush(['setVariable', 'signup-stage-dt', signupDt.value, 'session', false]);
    mouseFlowPush(['setVariable', 'signup-stage', signupStage.value, 'session', false]);

    if (parseInt(signupNumAttempts.value) > 1) {
      mouseFlowPush([
        'setVariable',
        'signup-stage-num-attempts',
        `${signupNumAttempts}`,
        'session',
        false,
      ]);
    }
  }
};

const crossSessionReadyDecorations = () => {
  const { isDeveloper } = useEnvContext();
  const { isLoggedIn } = useCrossSession();
  const { personalizationProfileStorable } = useWwwStorage();

  const loggedIn = isLoggedIn ? 'yes' : 'no';
  mouseFlowPush(['setVariable', 'logged-in', loggedIn]);
  cookieUpdates();

  // If you add more decorations you MUST contact whoever is managing GoogleTagManager
  const decorations: { [key: string]: boolean | string | null | number } = {
    'usr-dec-is-developer': `${isDeveloper}`,
    'usr-dec-is-onshape-user': haveSeenOn.value,
    'usr-dec-is-logged-in': !!isLoggedIn,
    'usr-dec-plan-group':
      personalizationProfileStorable.value?.currentUserData.activePlanGroup || '',
    'usr-dec-product-sku': personalizationProfileStorable.value?.currentUserData.activePlanId || '',
    'usr-dec-signup-stage': signupStage.value || '',
    'usr-dec-signup-stage-page': signupPage.value || '',
    'usr-dec-signup-stage-plan': signupPlan.value || '',
    'usr-dec-signup-stage-dt': signupDt.value || '',
    'usr-dec-signup-stage-num-attempts': signupNumAttempts.value || '',
  };

  // remove any undefined or empty values
  const filtered = Object.fromEntries(
    Object.entries(decorations).filter(
      ([, vv]) => typeof vv !== 'undefined' && vv?.toString().trim()
    )
  );

  const lastPushedDecorationsDate =
    personalizationProfileStorable.value?.lastPushedDecorationsDateAdded || 0;
  const dt = Date.now() - lastPushedDecorationsDate;
  const lastPushedDecorations = personalizationProfileStorable.value?.lastPushedDecorations || {};

  if (dt < userDecorationsPushThrottle && isObjectsEqual(filtered, lastPushedDecorations)) {
    console.log('user decorations throttled, nothing new');
    return;
  }

  dataLayerPush({
    event: 'HekateUserDecoration',
    ...filtered,
  });

  if (personalizationProfileStorable.value) {
    personalizationProfileStorable.value.lastPushedDecorations = filtered;
    personalizationProfileStorable.value.lastPushedDecorationsDateAdded = Date.now();
  }
};

/** Update decorations when isUserInit changes */
const watchInitUserCallback = useDebounceFn(() => {
  crossSessionReadyDecorations();
}, 500);

let haveSentUtmParams = false;
const sendUtmParams = useDebounceFn(() => {
  if (haveSentUtmParams || Object.entries(waUtmParamNames).length == 0) {
    return;
  }
  haveSentUtmParams = true;
  waPush({
    event: 'UtmParams',
    ...waUtmParamValues,
  });
}, 1000);

const initHekateEventBusListeners = () => {
  if (import.meta.server) return;

  // listen for form-inject-value events to capture utm params
  useHekateEventBus().addEventListener('form-inject-value', (evt) => {
    if (!waUtmParamNames.includes(evt.data.fieldName)) {
      return;
    }
    waUtmParamValues[evt.data.fieldName] = evt.data.fieldValue;
    sendUtmParams();
  });

  useHekateEventBus().addEventListener('wa-push', (evt) => {
    const { data: waData, event } = evt.data;
    waPush({ ...waData, event });
  });

  useHekateEventBus().addEventListener('uss-screen-shown', (evt) => {
    waPush({
      event: 'UssScreenShown',
      screenName: evt.data.screenName,
    });
  });

  useHekateEventBus().addEventListener('form-successfully-submitted', (evt) => {
    console.log({ data: evt.data.formDefinition });
    const { formDefinition } = evt.data as {
      formDefinition: {
        id: string;
        name: string;
        displayFields: Array<{ htmlName: string; val: string }>;
      };
    };

    const planId = formDefinition.displayFields.find(
      (field) => field.htmlName === FieldHtmlNameUtil.planId
    )?.val as CadPlanId;

    console.log({ eventData: evt.data, planId });

    waPush({
      event: 'FormSuccessfullySubmitted',
      formName: formDefinition.name,
      formId: formDefinition.id,
      planId: planId || '',
    });
  });
};

let isInit = false;
const init = () => {
  if (import.meta.server) return;
  if (isInit) return;
  isInit = true;

  // on page load, check for utm params in query
  if (window) {
    let foundAny = false;
    for (const paramName of waUtmParamNames) {
      const urlParams = new URLSearchParams(window.location.search);
      const paramValue = urlParams.get(paramName);
      if (paramValue) {
        foundAny = true;
        waUtmParamValues[paramName] = paramValue;
      }
    }
    if (foundAny) {
      sendUtmParams();
    }
  }

  initHekateEventBusListeners();
  waPush({
    event: 'WaPageView',
    pathname: window.location.pathname,
    buildVersion: BuildTags.packageVersion,
    buildHash: BuildTags.buildHash,
  });

  const { isUserInit, heartbeat } = useCrossSession();
  const { countryPersona, regionPersona, isPaid, productUsagePersona } = usePersonalization();
  watch(
    [isUserInit],
    () => {
      waPush({
        event: 'PersonaInfo',
        countryPersona: countryPersona.value,
        regionPersona: regionPersona.value,
        isPaid: isPaid.value,
        productUsagePersona: productUsagePersona.value.persona,
        countryCode: heartbeat.value?.countryCode || '',
        region: heartbeat.value?.region || '',
        currency: heartbeat.value?.currency || '',
      });
    },
    {
      immediate: false,
      once: true,
    }
  );

  window.addEventListener('beforeunload', () => {
    waPush({
      event: 'WaPageUnload',
      ua: navigator.userAgent,
      pathname: window.location.pathname,
      timeOnPage: (performance.now() / 1000).toFixed(2),
    });
  });
};

/** Utils for interacting with data layer objects. */
export const useTracking = () => {
  init();

  const { isUserInit } = useCrossSession();

  watch(isUserInit, watchInitUserCallback);

  return { mouseFlowPush, dataLayerPush, initClickTracking, waPush };
};
